/**
 * Modulo per la gestione del form "Vendi Corporate"
 * Permette agli utenti (autenticati o meno) di inviare richieste di contatto per vendite corporate
 */

import Page from "~/pages/Page";
import { getApi } from "~/auth";
import UI from "~/ui";
import $ from "jquery";
import { generaRecaptcha, recaptchaReady, recaptchaToken } from "~/recaptcha";
import { subscribeNewsletterWithConfirm, unsubscribeNewsletter } from "~/mailup";
import { ConsensiHelper } from "~/services/ConsensiHelper";
import { initSwiperCarousel } from "~/components/SwiperCarousel";

/**
 * Interfaccia per la risposta della chiamata API
 */
interface ApiResponse {
    success: boolean;
    responseText?: string;
    message?: string;
}

interface Consenso {
    id: number;
    codice: string;
    consenso: boolean;
    obbligatorio: boolean;
    scaduto: boolean;
}

/**
 * Classe per la gestione della pagina Vendi Corporate
 */
class VendiCorporate extends Page {

    private theForm: HTMLFormElement | null = null;
    private auth: any = null;
    private userInfo: any = null;
    private isUserAuthenticated: boolean = false;
    private isSubmitting: boolean = false;
    private recaptchaGenerated: boolean = false;

    constructor() {
        super("vendicorp", "VendiCorporate");
    }

    /**
     * Inizializza la pagina
     */
    pageStart(): Promise<void> {
        this.auth = getApi();
        this.userInfo = this.auth.getCached("userInfo");
        this.isUserAuthenticated = this.userInfo !== null && this.userInfo !== undefined && this.userInfo.anagraficaId > 0;

        this.initSwiperVendiCorporate();
        this.theForm = document.getElementById('form-vendicorporate') as HTMLFormElement;

        if (!this.theForm) {
            console.warn("Form vendi-corporate non trovato");
            return Promise.resolve();
        }

        // Inizializza il form con i dati dell'utente se loggato
        this.initFormVendiCorporate();

        // Gestisce la submit del form
        this.initFormSubmit();

        // Genera il reCAPTCHA
        this.generateRecaptcha();

        return Promise.resolve();
    }

    /**
  * ✅ Resetta completamente lo stato del form
  */
    private resetFormState(): void {
        if (!this.theForm) return;

        // ✅ Usa requestAnimationFrame per assicurarsi che il DOM sia pronto
        requestAnimationFrame(() => {
            if (!this.theForm) return;

            // ✅ APPROCCIO AGGRESSIVO: Rimuove tutte le classi di validazione
            this.theForm.classList.remove('was-validated', 'needs-validation');

            // ✅ Forza anche con jQuery
            $(this.theForm).removeClass('was-validated needs-validation');

            // ✅ Rimuove anche l'attributo novalidate temporaneamente e poi lo ri-aggiunge
            const hadNoValidate = this.theForm.hasAttribute('novalidate');
            this.theForm.removeAttribute('novalidate');

            // ✅ Rimuove tutte le classi di validazione dai singoli campi
            $(this.theForm).find("input, select, textarea").each(function () {
                const $element = $(this);

                // Rimuove le classi di stato
                $element.removeClass("is-invalid is-valid");

                // Rimuove attributi aria
                $element.removeAttr("aria-invalid aria-describedby");

                // Nasconde i feedback
                $element.siblings(".invalid-feedback").hide();
                $element.siblings(".valid-feedback").hide();
                $element.next(".invalid-feedback").hide();
                $element.next(".valid-feedback").hide();
            });

            // ✅ Nasconde tutti i messaggi nel form
            $(this.theForm).find(".invalid-feedback, .valid-feedback").hide();

            // ✅ Ripristina novalidate se c'era
            if (hadNoValidate) {
                this.theForm.setAttribute('novalidate', '');
            }

            console.info("✅ Stato del form resettato completamente");
        });
    }

    /**
     * Genera il reCAPTCHA
     */
    private async generateRecaptcha(): Promise<void> {
        try {
            await recaptchaReady();

            // Genera il token reCAPTCHA con action "vendicorporate"
            // Il token viene automaticamente salvato nell'input hidden #ggtoken
            await generaRecaptcha("vendicorporate");

            this.recaptchaGenerated = true;
            console.info('reCAPTCHA generato per il form vendi-corporate');
        } catch (error) {
            console.error('Errore durante la generazione del reCAPTCHA:', error);
            this.recaptchaGenerated = false;
        }
    }

    /**
     * Inizializza il form con i dati dell'utente autenticato (se presente)
     */
    private initFormVendiCorporate(): void {
        if (this.isUserAuthenticated && this.userInfo) {
            // Precompila i campi con i dati dell'utente
            $("#nome-vendi-corporate").val(this.userInfo.nome || "");
            $("#cognome-vendi-corporate").val(this.userInfo.cognome || "");
            $("#email-vendi-corporate").val(this.userInfo.email || "");

            // ✅ Recupera il telefono o cellulare dall'array contatti
            const telefono = this.getTelefonoFromUserInfo();
            if (telefono) {
                $("#telefono-vendi-corporate").val(telefono);
            }

            // ✅ Imposta il checkbox privacy come checked e disabilitato (non modificabile)
            $("#privacy-vendi-corporate")
                .prop("checked", true)
                .prop("disabled", true)
                .removeAttr("required");

            const hasNewsConsent = this.userInfo.consensi?.find((c: Consenso) => c.codice === 'NEWS')?.concesso ?? false;
            $("#newsletter-vendi-corporate").prop("checked", hasNewsConsent);

            const hasTerziConsent = this.userInfo.consensi?.find((c: Consenso) => c.codice === 'TERZI')?.concesso ?? false;
            $("#societapartner-vendi-corporate").prop("checked", hasTerziConsent);

            console.info("Utente autenticato:", this.userInfo.nome, this.userInfo.cognome);
        } else {
            // Aggiungi il required al checkbox privacy per utenti non autenticati
            const $chkPrivacy = $("#privacy-vendi-corporate");
            if (!$chkPrivacy.attr("required")) {
                $chkPrivacy
                    .prop("required", true)
                    .prop("disabled", false)
                    .prop("checked", false);
            }
        }
    }

    /**
     * ✅ Recupera il numero di telefono o cellulare dall'array contatti dell'utente
     * Priorità: Cellulare (CEL) > Telefono (TEL)
     */
    private getTelefonoFromUserInfo(): string | null {
        if (!this.userInfo || !this.userInfo.contatti || !Array.isArray(this.userInfo.contatti)) {
            return null;
        }

        // Cerca prima un cellulare (CEL)
        const cellulare = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'CELL' && c.valore
        );

        if (cellulare) {
            return this.formatTelefono(cellulare);
        }

        // Se non c'è cellulare, cerca un telefono (TEL)
        const telefono = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'TEL' && c.valore
        );

        if (telefono) {
            return this.formatTelefono(telefono);
        }

        return null;
    }

    /**
     * ✅ Formatta il numero di telefono con eventuale prefisso
     */
    private formatTelefono(contatto: any): string {
        if (!contatto || !contatto.valore) {
            return '';
        }

        // Se c'è un prefisso, lo concatena
        if (contatto.prefisso && contatto.prefisso.trim() !== '') {
            return `${contatto.prefisso} ${contatto.valore}`;
        }

        return contatto.valore;
    }

    /**
     * Inizializza la gestione della submit del form
     */
    private initFormSubmit(): void {
        if (!this.theForm) return;

        this.theForm.addEventListener('submit', async (event: Event) => {
            event.preventDefault();
            event.stopPropagation();

            // Impedisce il doppio click
            if (this.isSubmitting) {
                console.log("Invio già in corso...");
                return;
            }

            if (this.theForm && this.theForm.checkValidity()) {
                await this.richiestaContattoVendiCorporate();
            } else if (this.theForm) {
                this.theForm.classList.add('was-validated');
            }

        }, false);
    }

    /**
     * Invia la richiesta di contatto per vendita corporate
     */
    private async richiestaContattoVendiCorporate(): Promise<void> {
        if (!this.theForm) return;

        // Verifica il reCAPTCHA prima di procedere
        if (!this.recaptchaGenerated) {
            UI.confirmOneButton("", "Errore: reCAPTCHA non generato");
            return;
        }

        // Ottieni il token dal campo hidden
        const recaptchaTokenValue = recaptchaToken();

        if (!recaptchaTokenValue) {
            UI.confirmOneButton("", "Completa la verifica reCAPTCHA");
            return;
        }

        // Imposta il flag per impedire doppi click
        this.isSubmitting = true;
        this.disableSubmitButton(true);

        // Serializza i dati del form
        const formData: Record<string, any> = {};
        $(this.theForm)
            .serializeArray()
            .forEach((field) => {
                formData[field.name] = field.value;
            });

        // Aggiungi il token reCAPTCHA
        formData["ggtoken"] = recaptchaTokenValue;
        formData["action"] = "vendicorporate";

        console.info("Invio form vendi-corporate:", formData);

        this.spinner.spin();

        try {
            const url = `${window.BaseUrlWebApi}Mailing/RichiestaContattoVendiCorporate`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                credentials: 'include',
                body: JSON.stringify(formData)
            });

            const data: ApiResponse = await response.json();

            if (data.success) {

                //Aggiornamento consensi per utenti autenticati
                if (this.isUserAuthenticated) {
                    await ConsensiHelper.saveAndSyncConsensi(
                        "#form-vendicorporate",
                        this.userInfo.email || ""
                    );
                } else {
                    // Se l'utente ha selezionato la newsletter, effettua l'iscrizione
                    if ($("#newsletter-vendi-corporate").prop('checked')) {
                        await subscribeNewsletterWithConfirm(
                            (window as any).ATP_ListID_Newsletter,
                            (window as any).ATP_GroupId_FormContatto,
                            formData["Email"] || "",
                            formData["Nome"] || "",
                            formData["Cognome"] || "",
                            formData["Telefono"] || "");
                    }
                }

                // Reset del form
                this.theForm.reset();

                // ✅ Se l'utente è loggato, ricarica i dati nel form
                if (this.isUserAuthenticated) {
                    this.auth.clearCached("userInfo");
                    this.userInfo = this.auth.getCached("userInfo");
                    this.initFormVendiCorporate();
                }

                // ✅ Resetta lo stato del form DOPO aver ricaricato i dati
                // (rimuove tutte le classi di validazione, incluse quelle aggiunte automaticamente)
                this.resetFormState();

                // Mostra conferma
                UI.confirmOneButton(
                    "Richiesta inviata",
                    "E-mail correttamente inviata, sarete ricontattati prima possibile"
                );

                // Rigenera il reCAPTCHA per il prossimo utilizzo
                await this.generateRecaptcha();
            }
            else {
                const errorMessage = data.responseText || data.message || "Errore sconosciuto";
                UI.confirmOneButton("", `Errore: Non è stato possibile inviare la E-mail (${errorMessage})`);

                // Rigenera il reCAPTCHA in caso di errore
                await this.generateRecaptcha();
            }

        } catch (error: any) {
            console.error("Errore durante l'invio della richiesta:", error);
            const errorMessage = error.message || "Si è verificato un errore durante l'invio";
            UI.confirmOneButton("", `Errore durante l'invio della richiesta: ${errorMessage}`);

            // Rigenera il reCAPTCHA in caso di errore
            await this.generateRecaptcha();
        } finally {
            this.spinner.stop();
            this.isSubmitting = false;
            this.disableSubmitButton(false);
        }
    }

       /**
     * Disabilita/abilita il pulsante di submit
     */
    private disableSubmitButton(disable: boolean): void {
        const $submitButton = $("#invia-vendi-corporate");

        if (disable) {
            $submitButton.prop('disabled', true);
            // Aggiunge uno spinner al pulsante se disponibile
            const originalText = $submitButton.html();
            $submitButton.data('original-text', originalText);
            $submitButton.html('<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Invio in corso...');
        } else {
            $submitButton.prop('disabled', false);
            // Ripristina il testo originale
            const originalText = $submitButton.data('original-text');
            if (originalText) {
                $submitButton.html(originalText);
            }
        }
    }

    /**
     * Inizializza lo Swiper della sezione VendiCorporate (.swiperVendiCorporate)
     */
    private initSwiperVendiCorporate(): void {
        initSwiperCarousel('.swiperVendiCorporate', { base: 1.25, bp480: 1.25, bp576: 1.75, bp768: 2.25, bp992: 2.75, bp1200: 3.25, bp1400: 4, });
    }
}

export default VendiCorporate;
